import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Switch } from "@/components/ui/switch";
import { CheckCircle2, Star, Sparkles, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const PLANS = [
  { key: "free", price: "Free", name: "Free Membership", tagline: "For NRI members only", features: ["Create full profile", "Browse & search with filters", "Shortlist profiles", "Express interest — 10/month"], popular: false, color: "border-[#EAE3D2]", free: true },
  { key: "visitor_79", price: "£79", name: "India Resident", tagline: "India-based, seeking NRI match", features: ["Full search access — 6 months", "Express interest — 10/month", "See verified NRI profiles", "Upgrade anytime"], popular: false, color: "border-[#EAE3D2]" },
  { key: "basic_199", price: "£199", name: "Connect", tagline: "Contact & chat", features: ["Everything in Free", "View contact details (mobile & addresses)", "Chat with mutual matches", "Express interest — 20/month"], popular: false, color: "border-[#EAE3D2]" },
  { key: "premium_399", price: "£399", name: "Verified", tagline: "Immigration transparency", features: ["Everything in Connect", "Visa Verified badge visibility", "Immigration details on mutual interest", "Full government immigration check — reference, validity & conditions", "Visa share code access"], popular: true, color: "border-[#D4AF37] border-2" },
  { key: "elite_699", price: "£699", name: "Elite Match-Maker", tagline: "White-glove service", features: ["Everything in Verified", "Dedicated human match-maker", "Weekly AI compatibility insights (Claude)", "Priority listing in search", "Priority support"], popular: false, color: "border-[#6B1D2F] border-2" },
];

export default function Pricing() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [autoRenew, setAutoRenew] = useState(false);
  // NRI members (logged in) already have free access — hide the Free tier for them.
  // India-based members never see Free (they need the India Resident £79 plan). Non-logged visitors see all plans.
  const PLAN_AMOUNTS = { free: 0, visitor_79: 79, basic_199: 199, premium_399: 399, elite_699: 699 };
  let visiblePlans = user?.member_type === "india"
    ? PLANS.filter((p) => p.key !== "free")
    : user?.member_type === "nri"
    ? PLANS.filter((p) => p.key !== "free" && p.key !== "visitor_79")
    : PLANS;
  // Logged-in users only see plans priced above their current plan.
  if (user) {
    const current = PLAN_AMOUNTS[user.plan] ?? 0;
    visiblePlans = visiblePlans.filter((p) => (PLAN_AMOUNTS[p.key] ?? 0) > current);
  }

  const upgrade = async (plan_key) => {
    if (!user) { nav("/login"); return; }
    try {
      const r = await api.post("/payments/checkout", { plan_key, origin_url: window.location.origin, auto_renew: autoRenew });
      window.location.href = r.data.checkout_url;
    } catch (e) { toast.error(formatApiError(e)); }
  };

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
      <div className="text-center max-w-2xl mx-auto">
        <p className="text-xs uppercase tracking-[0.22em] font-semibold text-[#B8860B]">Plans that fit your journey</p>
        <h1 className="font-serif text-4xl sm:text-5xl font-semibold text-slate-900 mt-2">Free to join. Upgrade to connect.</h1>
        <p className="text-slate-600 mt-3">All paid plans last 6 months. Renewing extends by another 6 months from the purchase date.</p>
        <div className="mt-6 inline-flex items-center gap-3 bg-white border border-[#EAE3D2] rounded-full px-5 py-2.5 shadow-sm">
          <Switch checked={autoRenew} onCheckedChange={setAutoRenew} id="auto-renew" data-testid="auto-renew-toggle" />
          <label htmlFor="auto-renew" className="text-sm text-slate-800 cursor-pointer">
            <b>Auto-renew</b> — never lose access mid-conversation
          </label>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-5 gap-5 mt-12">
        {visiblePlans.map((p) => (
          <Card key={p.key} className={`p-6 bg-white ${p.color} ${p.popular ? "plan-card-featured shadow-2xl" : "shadow-sm"} relative flex flex-col`} data-testid={`plan-card-${p.key}`}>
            {p.popular && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#D4AF37] text-slate-900 font-semibold px-4 whitespace-nowrap"><Star className="w-3 h-3 mr-1" /> Most Popular</Badge>}
            {p.key === "elite_699" && <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[#6B1D2F] text-white font-semibold px-4 whitespace-nowrap"><Sparkles className="w-3 h-3 mr-1" /> With Match-Maker</Badge>}
            <p className="text-xs uppercase tracking-widest font-semibold text-slate-500">{p.tagline}</p>
            <h3 className="font-serif text-2xl text-slate-900 mt-1">{p.name}</h3>
            <div className="mt-3">
              <span className="font-serif text-4xl font-bold text-[#6B1D2F]">{p.price}</span>
              {!p.free && <span className="text-slate-500 text-xs ml-1">/ 6 months</span>}
            </div>
            <ul className="mt-5 space-y-2.5 flex-1">
              {p.features.map((f) => (
                <li key={f} className="flex items-start gap-2 text-slate-700 text-[13px] leading-snug">
                  <CheckCircle2 className="w-4 h-4 text-emerald-700 mt-0.5 shrink-0" /> {f}
                </li>
              ))}
            </ul>
            {p.free ? (
              <Button data-testid="plan-buy-free" onClick={() => nav("/register")} variant="outline" className="mt-6 w-full rounded-full py-5 border-[#6B1D2F]/40 text-[#6B1D2F] font-semibold">
                Get started free
              </Button>
            ) : (
              <Button data-testid={`plan-buy-${p.key}`} onClick={() => upgrade(p.key)} className={`mt-6 w-full rounded-full py-5 text-sm font-semibold ${p.popular ? "bg-[#D4AF37] hover:bg-[#B8860B] text-slate-900" : "bg-[#6B1D2F] hover:bg-[#541624] text-white"}`}>
                {user ? `Choose ${p.name}` : "Sign in to upgrade"}
              </Button>
            )}
          </Card>
        ))}
      </div>

      {user && visiblePlans.length === 0 && (
        <div className="mt-10 max-w-xl mx-auto text-center bg-[#F8F4EC] border border-[#EAE3D2] rounded-2xl p-6" data-testid="plans-top-tier">
          <p className="text-slate-700">You're on our top plan — there's nothing higher to upgrade to. 🎉</p>
        </div>
      )}

      <div className="mt-10 max-w-3xl mx-auto text-center bg-emerald-50/60 border border-emerald-200 rounded-2xl p-6">
        <ShieldCheck className="w-6 h-6 mx-auto text-emerald-700" />
        <p className="mt-2 text-sm text-slate-700">
          <b>The Verified (£399) plan is the only matrimony membership that shows you a live government immigration check</b> — official reference, visa validity and conditions — on profiles you match with.
        </p>
      </div>

      <p className="text-center text-xs text-slate-500 mt-8">Secure checkout by Stripe · Cards, Apple Pay &amp; Google Pay accepted</p>
    </div>
  );
}
